import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { EModulePermissions, Permission} from '../enums/permission.enum';
import { Router } from '@angular/router';
import { IUser } from '../interfaces/user.interface';
import { RoleEnum } from '../enums/role.enum';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private permissions = new Map<string, boolean>();

  constructor(private authService: AuthService, private router: Router) {
  }

  /**
   * Een super-admin heeft geen rechtenlijst (de server slaat elke controle
   * voor hem over), dus een knop die alleen op hasPermission() leunt
   * verdwijnt juist voor de beheerder. Voor knoppen die de server met
   * [RequireAction] bewaakt: dit gebruiken.
   */
  public isSuperAdmin(): boolean {
    const user = this.authService?.user;
    // "Open als club": token-rol is club_admin, maar impersonated_by markeert
    // de echte super_admin (GlobalPlayer-koppeling e.d.).
    const role = user?.role;
    const key = typeof role === 'string' ? role : role?.key;
    if (user?.impersonated_by && key === RoleEnum.CLUB_ADMIN) {
      return true;
    }
    return key === RoleEnum.SUPER_ADMIN;
  }

  /**
   * Een spelersaccount (rol club_player): begint op zijn eigen profiel (/me,
   * docs/SPELERSPROFIEL.md) in plaats van bij de wedstrijden van de club.
   */
  public isSpeler(): boolean {
    const role = this.authService?.user?.role;
    const key = typeof role === 'string' ? role : role?.key;
    return key === RoleEnum.CLUB_PLAYER;
  }

  public hasPermissionOrSuperAdmin(permission: Permission | string): boolean {
    return this.isSuperAdmin() || this.hasPermission(permission);
  }

  public hasPermission(permission: Permission | string): boolean {
    const user = this.authService?.user;

    // If user is already logged in and doesn't have actions. It can happen for already logged user
    if (!user) {
      return false;
    }

    if (user && !user?.action) {
      this.authService.logout();
      this.router.navigateByUrl('/auth/login');
      return false;
    }

    return this.saveAndGetPermission(user, permission);
  }

  /**
   * Is permission exist, return it.
   * Otherwise, set and return
   * @param user
   * @param permission
   * @private
   */
  private saveAndGetPermission(user: IUser, permission: Permission | string): boolean {
    if (this.permissions.get(permission)) {
      return this.permissions.get(permission);
    }

    const action = user.action.find(action => action === permission);

    this.permissions.set(permission, !!action);

    return this.permissions.get(permission);
  }

  /**
   * Check if user has permission to module
   * @param modulePermission
   * @private
   */
  public checkModulePermission(modulePermission: EModulePermissions | string): boolean {
    const user = this.authService?.user;

    // If user is already logged in and doesn't have actions. It can happen for already logged user
    if (!user) {
      return false;
    }

    const action = user.modules?.find(action => action === modulePermission);

    if (!action) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * Heeft deze club minstens één module die de webapp een beginpagina geeft?
   *
   * Zonder `video_storage` én zonder `scheduling` is er geen pagina om naar
   * toe te gaan: /videos verwijst dan door naar /live (permission.guard.ts)
   * en /live weer terug naar /videos. Die lus annuleert elke navigatie
   * halverwege en rendert dus nooit iets — het scherm bleef leeg en grijs,
   * zonder fout in de console (Al-Arabi SC, 22-09-2026).
   *
   * Een super-admin hoort hier niet in te lopen: die heeft geen club en dus
   * geen modules in zijn token.
   */
  public heeftPlatformToegang(): boolean {
    if (this.isSuperAdmin()) {
      return true;
    }

    return this.checkModulePermission(EModulePermissions.VIDEO_STORAGE)
      || this.checkModulePermission(EModulePermissions.SCHEDULING);
  }

}
